import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

/**
 * A slim progress bar across the top of the app while a route change is in
 * flight.
 *
 * Why: every authenticated page has `getServerSideProps`, so Next holds the URL
 * (and the whole screen) until a serverless round-trip resolves. Until this
 * existed nothing on screen moved during that window, so a slow response looked
 * exactly like a button that had not registered the click — which is how the
 * back button "stopped working" during a demo.
 *
 * Deliberately quiet: nothing shows for the first 120 ms, so a fast navigation
 * does not flash a bar.
 */
const SHOW_AFTER_MS = 120;

const RouteProgress = () => {
  const router = useRouter();
  const [state, setState] = useState("idle"); // idle | pending | done
  const showTimer = useRef(null);
  const hideTimer = useRef(null);

  useEffect(() => {
    const clear = () => {
      clearTimeout(showTimer.current);
      clearTimeout(hideTimer.current);
    };

    const start = () => {
      clear();
      showTimer.current = setTimeout(() => setState("pending"), SHOW_AFTER_MS);
    };

    const stop = () => {
      clear();
      // Run the bar out to full width before removing it, so a navigation that
      // did show a bar never snaps away mid-track.
      setState(prev => (prev === "pending" ? "done" : "idle"));
      hideTimer.current = setTimeout(() => setState("idle"), 250);
    };

    router.events.on("routeChangeStart", start);
    router.events.on("routeChangeComplete", stop);
    router.events.on("routeChangeError", stop);
    return () => {
      clear();
      router.events.off("routeChangeStart", start);
      router.events.off("routeChangeComplete", stop);
      router.events.off("routeChangeError", stop);
    };
  }, [router.events]);

  if (state === "idle") return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] h-0.5 pointer-events-none"
      role="progressbar"
      aria-label="Loading page"
      data-testid="route-progress"
    >
      <div
        className="h-full bg-primary"
        style={{
          width: state === "done" ? "100%" : "70%",
          opacity: state === "done" ? 0 : 1,
          transition:
            state === "done"
              ? "width 150ms linear, opacity 200ms ease-out"
              : "width 1.6s cubic-bezier(0.1, 0.8, 0.2, 1)"
        }}
      />
    </div>
  );
};

export default RouteProgress;
